import { useState, useEffect, useCallback } from "react";
import {
  Plus, Search, LayoutGrid, List, MoreVertical, BarChart3, LineChart,
  PieChart, Table2, Gauge, Activity, Copy, Trash2, Edit, Loader2,
  Database, ChevronRight, Sparkles,
} from "lucide-react";
import {
  LineChart as ReLineChart, Line, BarChart as ReBarChart, Bar,
  PieChart as RePieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { widgetsApi } from "@/lib/api";
import { toast } from "sonner";
import { HelpIcon } from "@/components/HelpIcon";

const WIDGET_TYPES = [
  { id: "kpi", name: "KPI Card", icon: Activity },
  { id: "line", name: "Line Chart", icon: LineChart },
  { id: "bar", name: "Bar Chart", icon: BarChart3 },
  { id: "pie", name: "Pie Chart", icon: PieChart },
  { id: "gauge", name: "Gauge", icon: Gauge },
  { id: "table", name: "Data Table", icon: Table2 },
];

const COLLECTIONS = [
  { id: "audit_logs", label: "audit_logs", desc: "API events, auth logs" },
  { id: "users", label: "users", desc: "Platform users" },
  { id: "tenants", label: "tenants", desc: "Tenant organizations" },
  { id: "api_keys", label: "api_keys", desc: "API key records" },
  { id: "payment_transactions", label: "payment_transactions", desc: "Billing history" },
];

const TEMPLATES = [
  {
    name: "Events by Day",
    collection: "audit_logs",
    type: "line",
    pipeline: JSON.stringify([
      { "$group": { "_id": { "$substr": ["$created_at", 0, 10] }, "requests": { "$sum": 1 }, "failures": { "$sum": { "$cond": [{ "$eq": ["$success", false] }, 1, 0] } } } },
      { "$sort": { "_id": 1 } },
      { "$limit": 30 },
      { "$project": { "_id": 0, "date": "$_id", "requests": 1, "failures": 1 } }
    ], null, 2),
  },
  {
    name: "Users by Role",
    collection: "users",
    type: "pie",
    pipeline: JSON.stringify([
      { "$group": { "_id": "$role", "count": { "$sum": 1 } } },
      { "$project": { "_id": 0, "name": "$_id", "value": "$count" } },
      { "$sort": { "value": -1 } }
    ], null, 2),
  },
  {
    name: "API Keys Status",
    collection: "api_keys",
    type: "pie",
    pipeline: JSON.stringify([
      { "$group": { "_id": "$is_active", "count": { "$sum": 1 } } },
      { "$project": { "_id": 0, "name": { "$cond": ["$_id", "Active", "Inactive"] }, "value": "$count" } }
    ], null, 2),
  },
  {
    name: "Top Tenants by Usage",
    collection: "tenants",
    type: "bar",
    pipeline: JSON.stringify([
      { "$sort": { "api_calls_total": -1 } },
      { "$limit": 10 },
      { "$project": { "_id": 0, "name": 1, "value": "$api_calls_total" } }
    ], null, 2),
  },
  {
    name: "Platform KPI",
    collection: "audit_logs",
    type: "kpi",
    pipeline: JSON.stringify([
      { "$group": { "_id": null, "total": { "$sum": 1 }, "failures": { "$sum": { "$cond": [{ "$eq": ["$success", false] }, 1, 0] } } } },
      { "$project": { "_id": 0, "value": "$total", "label": { "$literal": "Total Audit Events" }, "sub": { "$concat": [{ "$toString": "$failures" }, " failures"] } } }
    ], null, 2),
  },
];

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

function ChartPreview({ result, widgetType }) {
  if (!result) {
    return (
      <div className="text-center text-slate-400 flex flex-col items-center justify-center h-full gap-2">
        <BarChart3 className="w-10 h-10" />
        <p className="text-sm">Run query to see a live preview</p>
      </div>
    );
  }
  if (!result.success) {
    return (
      <div className="text-center p-4">
        <p className="text-sm text-red-400 font-medium">Query error</p>
        <p className="text-xs text-red-300 mt-1 font-mono break-words">{result.error}</p>
      </div>
    );
  }

  const data = result.data;

  if (widgetType === "kpi" || (!Array.isArray(data) && data)) {
    const kpi = Array.isArray(data) ? null : data;
    if (kpi) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-1">
          <div className="text-5xl font-bold text-slate-900">{(kpi.value ?? 0).toLocaleString()}</div>
          <div className="text-sm text-slate-500 mt-1">{kpi.label}</div>
          {kpi.sub && <div className="text-xs text-slate-400">{kpi.sub}</div>}
        </div>
      );
    }
  }

  if (!Array.isArray(data) || data.length === 0) {
    return <div className="text-center text-slate-400 text-sm">No data returned from query</div>;
  }

  const keys = Object.keys(data[0]);
  const xKey = keys[0];
  const yKeys = keys.slice(1);
  const nameKey = "name" in data[0] ? "name" : xKey;
  const valKey = "value" in data[0] ? "value" : (yKeys[0] || xKey);

  if (widgetType === "pie") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <RePieChart>
          <Pie data={data} dataKey={valKey} nameKey={nameKey} cx="50%" cy="50%" outerRadius="60%"
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip />
        </RePieChart>
      </ResponsiveContainer>
    );
  }

  if (widgetType === "table") {
    return (
      <div className="overflow-auto h-full">
        <div className="bg-slate-50 sticky top-0 flex border-b border-slate-100">
          {keys.map(k => <div key={k} className="flex-1 min-w-0 px-2 py-1.5 text-left text-slate-500 uppercase text-[10px] font-medium">{k}</div>)}
        </div>
        {data.slice(0, 15).map((row, i) => (
          <div key={i} className="flex border-b border-slate-100 hover:bg-slate-50">
            {keys.map(k => <div key={k} className="flex-1 min-w-0 px-2 py-1.5 text-slate-700 text-xs truncate">{String(row[k] ?? "")}</div>)}
          </div>
        ))}
      </div>
    );
  }

  if (widgetType === "bar") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ReBarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey={xKey} tick={{ fontSize: 9 }} />
          <YAxis tick={{ fontSize: 9 }} />
          <Tooltip />
          {(yKeys.length > 0 ? yKeys : [valKey]).map((k, i) => <Bar key={k} dataKey={k} fill={COLORS[i % COLORS.length]} />)}
        </ReBarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ReLineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 9 }} />
        <YAxis tick={{ fontSize: 9 }} />
        <Tooltip />
        {(yKeys.length > 0 ? yKeys : [valKey]).map((k, i) => (
          <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} dot={false} strokeWidth={2} />
        ))}
      </ReLineChart>
    </ResponsiveContainer>
  );
}

export default function WidgetBuilder() {
  const [viewMode, setViewMode] = useState("grid");
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState(null);

  // Form state
  const [widgetName, setWidgetName] = useState("");
  const [widgetType, setWidgetType] = useState("line");
  const [collection, setCollection] = useState("audit_logs");
  const [pipeline, setPipeline] = useState("");
  const [refreshInterval, setRefreshInterval] = useState("5m");
  const [queryResult, setQueryResult] = useState(null);

  // Data
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningQuery, setRunningQuery] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fetch = useCallback(async () => {
    try { setWidgets(await widgetsApi.list()); }
    catch { toast.error("Failed to load widgets"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const filtered = widgets.filter(w =>
    w.name.toLowerCase().includes(search.toLowerCase()) &&
    (filterType === "all" || w.type === filterType)
  );

  const openCreate = () => {
    setWidgetName(""); setWidgetType("line"); setCollection("audit_logs");
    setPipeline(""); setRefreshInterval("5m");
    setSelectedWidget(null); setQueryResult(null); setDialogOpen(true);
  };

  const openEdit = (w) => {
    setWidgetName(w.name); setWidgetType(w.type);
    setCollection(w.collection || "audit_logs");
    setPipeline(w.pipeline_json || w.sql_query || "");
    setRefreshInterval(w.refresh_interval || "5m");
    setSelectedWidget(w); setQueryResult(null); setDialogOpen(true);
  };

  const applyTemplate = (tpl) => {
    setCollection(tpl.collection);
    setWidgetType(tpl.type);
    setPipeline(tpl.pipeline);
    setQueryResult(null);
  };

  const formatPipeline = () => {
    try {
      setPipeline(JSON.stringify(JSON.parse(pipeline), null, 2));
    } catch {
      toast.error("Invalid JSON — fix syntax errors first");
    }
  };

  const handleRunQuery = async () => {
    if (!pipeline.trim()) { toast.error("Enter a MongoDB aggregation pipeline first"); return; }
    setRunningQuery(true);
    try {
      const res = await widgetsApi.execute({ collection, pipeline_json: pipeline, widget_type: widgetType, limit: 50 });
      setQueryResult(res);
      if (res.success) toast.success(`Query returned ${res.rows} row(s) from ${res.collection}`);
      else toast.error(res.error || "Query failed");
    } catch (e) {
      toast.error(e.message || "Execution failed");
      setQueryResult({ success: false, error: e.message });
    } finally {
      setRunningQuery(false);
    }
  };

  const handleSave = async () => {
    if (!widgetName.trim()) { toast.error("Widget name is required"); return; }
    setSaving(true);
    try {
      const payload = { name: widgetName, type: widgetType, collection, pipeline_json: pipeline, sql_query: pipeline, refresh_interval: refreshInterval };
      if (selectedWidget) {
        await widgetsApi.update(selectedWidget.id, payload);
        toast.success("Widget updated");
      } else {
        await widgetsApi.create(payload);
        toast.success("Widget created");
      }
      setDialogOpen(false);
      fetch();
    } catch (e) {
      toast.error(e.message || "Failed to save widget");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (w) => {
    try {
      await widgetsApi.remove(w.id);
      toast.success("Widget deleted");
      fetch();
    } catch (e) {
      toast.error(e.message || "Failed to delete widget");
    }
  };

  const handleDuplicate = async (w) => {
    try {
      await widgetsApi.create({ name: `${w.name} (Copy)`, type: w.type, collection: w.collection || "audit_logs", pipeline_json: w.pipeline_json || "", sql_query: w.sql_query || "", refresh_interval: w.refresh_interval || "5m" });
      toast.success("Widget duplicated");
      fetch();
    } catch (e) {
      toast.error(e.message || "Failed to duplicate");
    }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const res = await widgetsApi.seedDefaults();
      toast.success(`${res.message}`);
      fetch();
    } catch (e) {
      toast.error(e.message || "Failed to seed defaults");
    } finally {
      setSeeding(false);
    }
  };

  const getIcon = (type) => WIDGET_TYPES.find(t => t.id === type)?.icon || Activity;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading widgets...
      </div>
    );
  }

  return (
    <div data-testid="widget-builder-page">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="widget-builder-title">Widget Builder</h1>
            <HelpIcon featureTag="widgets" portalType="admin" />
          </div>
          <p className="text-sm text-slate-500 mt-1">Create dashboard widgets backed by real MongoDB aggregation pipelines</p>
        </div>
        {widgets.length === 0 && (
          <Button variant="outline" onClick={handleSeedDefaults} disabled={seeding} data-testid="seed-widgets-btn">
            {seeding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Seeding...</> : <><Sparkles className="w-4 h-4 mr-2" /> Load Default Widgets</>}
          </Button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search widgets..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="widget-search" />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {WIDGET_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border rounded-md">
            <Button variant={viewMode === "grid" ? "secondary" : "ghost"} size="sm" onClick={() => setViewMode("grid")} className="rounded-r-none"><LayoutGrid className="w-4 h-4" /></Button>
            <Button variant={viewMode === "list" ? "secondary" : "ghost"} size="sm" onClick={() => setViewMode("list")} className="rounded-l-none"><List className="w-4 h-4" /></Button>
          </div>
          <Button onClick={openCreate} data-testid="create-widget-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Widget
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="text-center py-16 text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
          <BarChart3 className="w-10 h-10 mx-auto mb-3" />
          <p className="text-sm font-medium">{widgets.length === 0 ? "No widgets yet" : "No widgets match your filter"}</p>
          {widgets.length === 0 && (
            <div className="mt-4 flex gap-3 justify-center">
              <Button variant="outline" size="sm" onClick={handleSeedDefaults} disabled={seeding}>
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Load 8 Default Widgets
              </Button>
              <Button size="sm" onClick={openCreate}>
                <Plus className="w-4 h-4 mr-2" /> Create Manually
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Grid view */}
      {filtered.length > 0 && viewMode === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="widget-gallery-grid">
          {filtered.map(w => {
            const Icon = getIcon(w.type);
            return (
              <div key={w.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-testid={`widget-card-${w.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 text-sm">{w.name}</h3>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Database className="w-3 h-3 text-slate-400" />
                        <span className="text-xs text-slate-400 font-mono">{w.collection || "audit_logs"}</span>
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreVertical className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(w)}><Edit className="w-4 h-4 mr-2" /> Edit & Run</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDuplicate(w)}><Copy className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
                      <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(w)}><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="h-20 bg-slate-50 rounded-md mb-3 flex items-center justify-center">
                  <Icon className="w-8 h-8 text-slate-300" />
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <Badge variant="outline" className="text-[10px]">{w.type}</Badge>
                  <span>{w.refresh_interval || "5m"} refresh</span>
                </div>
                <p className="text-xs text-slate-400 mt-2 truncate">{w.description || "No description"}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* List view */}
      {filtered.length > 0 && viewMode === "list" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="widget-gallery-list">
          <div>
            <div className="grid grid-cols-[1fr_160px_100px_80px_160px] bg-slate-50 border-b border-slate-200">
              {["Widget","Collection","Type","Refresh","Actions"].map(h => (
                <div key={h} className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
              ))}
            </div>
            {filtered.map(w => {
              const Icon = getIcon(w.type);
              return (
                <div key={w.id} className="grid grid-cols-[1fr_160px_100px_80px_160px] border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <div className="px-4 py-3 flex items-center gap-3">
                    <Icon className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-900">{w.name}</span>
                  </div>
                  <div className="px-4 py-3 flex items-center"><code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">{w.collection || "audit_logs"}</code></div>
                  <div className="px-4 py-3 flex items-center"><Badge variant="outline" className="text-[10px]">{w.type}</Badge></div>
                  <div className="px-4 py-3 text-sm text-slate-600 flex items-center">{w.refresh_interval || "5m"}</div>
                  <div className="px-4 py-3 flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(w)}>Edit</Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(w)}>Delete</Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-6xl h-[88vh] flex flex-col p-0" aria-describedby="widget-dialog-desc">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <DialogTitle>{selectedWidget ? `Edit: ${selectedWidget.name}` : "Create New Widget"}</DialogTitle>
            <p id="widget-dialog-desc" className="text-sm text-slate-500 mt-1">
              Write a real MongoDB aggregation pipeline, run it, and preview the visualization.
            </p>
          </DialogHeader>

          <div className="flex-1 flex overflow-hidden min-h-0">
            {/* Left — Pipeline editor */}
            <div className="w-1/2 border-r flex flex-col">
              <Tabs defaultValue="editor" className="flex-1 flex flex-col">
                <div className="px-4 pt-4 pb-0 shrink-0">
                  <TabsList className="w-full">
                    <TabsTrigger value="editor" className="flex-1">Pipeline Editor</TabsTrigger>
                    <TabsTrigger value="templates" className="flex-1">Templates</TabsTrigger>
                    <TabsTrigger value="schema" className="flex-1">Schema</TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="editor" className="flex-1 p-4 m-0 flex flex-col min-h-0">
                  <div className="mb-3 shrink-0">
                    <Label className="text-xs text-slate-500 mb-1 block">Collection</Label>
                    <Select value={collection} onValueChange={setCollection}>
                      <SelectTrigger data-testid="collection-select">
                        <Database className="w-3 h-3 mr-2 text-slate-400" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COLLECTIONS.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            <div><span className="font-mono text-sm">{c.label}</span><span className="text-xs text-slate-400 ml-2">{c.desc}</span></div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Label className="text-xs text-slate-500 mb-1 block shrink-0">
                    MongoDB Aggregation Pipeline <span className="text-slate-300">(JSON array of stages)</span>
                  </Label>
                  <Textarea
                    value={pipeline}
                    onChange={(e) => { setPipeline(e.target.value); setQueryResult(null); }}
                    placeholder={`[\n  { "$group": { "_id": "$role", "count": { "$sum": 1 } } },\n  { "$project": { "_id": 0, "name": "$_id", "value": "$count" } }\n]`}
                    className="flex-1 font-mono text-xs resize-none min-h-0 bg-slate-900 text-emerald-300 border-slate-700 placeholder:text-slate-600"
                    data-testid="pipeline-editor"
                  />
                  <div className="flex items-center justify-between mt-2 shrink-0">
                    <Button size="sm" variant="ghost" className="text-xs" onClick={formatPipeline}>Format JSON</Button>
                    <Button size="sm" variant="secondary" onClick={handleRunQuery} disabled={runningQuery} data-testid="run-query-btn">
                      {runningQuery ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Running...</> : <><Activity className="w-3 h-3 mr-1" /> Run Query</>}
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="templates" className="flex-1 p-4 m-0 overflow-auto">
                  <p className="text-xs text-slate-500 mb-3">Click a template to load it into the editor</p>
                  <div className="space-y-3">
                    {TEMPLATES.map((tpl, i) => (
                      <button key={i} className="w-full p-3 border rounded-lg hover:bg-slate-50 text-left group transition-colors" onClick={() => applyTemplate(tpl)}>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-sm font-medium text-slate-900">{tpl.name}</span>
                            <span className="ml-2 text-xs text-slate-400 font-mono">{tpl.collection}</span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                        </div>
                        <pre className="text-xs text-slate-500 mt-2 font-mono overflow-hidden text-ellipsis whitespace-nowrap">
                          {tpl.pipeline.slice(0, 80)}...
                        </pre>
                      </button>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="schema" className="flex-1 p-4 m-0 overflow-auto">
                  <div className="space-y-4">
                    {COLLECTIONS.map(col => (
                      <div key={col.id}>
                        <div className="flex items-center gap-2 mb-2">
                          <Database className="w-3.5 h-3.5 text-slate-400" />
                          <code className="text-sm font-mono text-slate-800 font-semibold">{col.label}</code>
                          <span className="text-xs text-slate-400">{col.desc}</span>
                        </div>
                        <div className="pl-5 text-xs font-mono text-slate-500 space-y-0.5">
                          {({
                            audit_logs: ["created_at (ISO string)", "action", "success (bool)", "ip_address", "user_id", "details"],
                            users: ["created_at", "email", "role", "is_active (bool)", "full_name"],
                            tenants: ["created_at", "name", "status", "plan_id", "api_calls_total", "owner_id"],
                            api_keys: ["created_at", "name", "is_active (bool)", "user_id", "last_used", "environment"],
                            payment_transactions: ["created_at", "amount", "currency", "status", "tenant_id"],
                          })[col.id]?.map(f => <p key={f}>{f}</p>)}
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Right — Preview + config */}
            <div className="w-1/2 flex flex-col">
              <div className="flex-1 p-4 bg-slate-50 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-2 shrink-0">
                  <Label className="text-xs text-slate-500">Live Preview</Label>
                  {queryResult?.collection && (
                    <Badge variant="outline" className="text-[10px] font-mono">{queryResult.collection} · {queryResult.rows} rows</Badge>
                  )}
                </div>
                <div className="flex-1 bg-white border rounded-lg flex items-center justify-center p-4 min-h-0" data-testid="widget-preview">
                  <ChartPreview result={queryResult} widgetType={widgetType} />
                </div>
              </div>

              <div className="p-4 border-t space-y-3 shrink-0">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Widget Name</Label>
                    <Input value={widgetName} onChange={(e) => setWidgetName(e.target.value)} placeholder="My Widget" className="mt-1" data-testid="widget-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Chart Type</Label>
                    <Select value={widgetType} onValueChange={(v) => { setWidgetType(v); setQueryResult(null); }}>
                      <SelectTrigger className="mt-1" data-testid="chart-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {WIDGET_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Refresh Interval</Label>
                  <Select value={refreshInterval} onValueChange={setRefreshInterval}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[["10s","10 seconds"],["30s","30 seconds"],["1m","1 minute"],["5m","5 minutes"],["15m","15 minutes"],["1h","1 hour"]].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="publish-widget-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : (selectedWidget ? "Save Changes" : "Create Widget")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
